import { GetOneResponse, HttpError, OpenNotificationParams } from '@refinedev/core'
import { UseQueryOptions } from '@tanstack/react-query'
import { FormRule, Typography } from 'antd'
import { IBankAccount } from '../interfaces/bankAccounts'
import { IBank, IWallet } from '../interfaces/banks'
import { ICompany } from '../interfaces/companies'
import { ICurrencyToken } from '../interfaces/currencyToken'
import { ICustomer } from '../interfaces/customer'
import { INetwork } from '../interfaces/network'
import { IRemitter } from '../interfaces/remitter'
import {
  PaymentOrderEditForm,
  PaymentRequestsEditForm,
} from '../pages/components/interface'
import ICurrency from '../pages/currencies/currency.interface'
import PaymentStatus from '../types/paymentStatus.type'
import { FieldValue } from './formFields/autoComplete'

export const errorNotification = (
  error?: HttpError
): OpenNotificationParams => {
  return {
    type: 'error',
    message: 'Error updating',
    description: error?.message,
  }
}

export const buildCurrencyValue = ({ code, name }: ICurrency) =>
  `${code} - ${name}`

export const buildCustomerValue = ({ fullName, smartUID, email, id }: ICustomer) => {
  const primaryName = fullName || 'Unknown customer'
  const secondary = email || smartUID || `ID ${id ?? '-'}`
  return `${primaryName} - ${secondary}`
}

export const buildBankAccountValue = ({
  accountNumber,
  bank,
  id,
}: IBankAccount) => `${accountNumber} - ${bank?.name} - ${bank?.country ?? bank?.legalAddress} - ${bank?.swift} - ${id}`

export const mapCurrencyItem = (currency: ICurrency, index: number) => {
  const value = buildCurrencyValue(currency)
  return {
    value,
    key: index,
    label: <Typography.Text>{value}</Typography.Text>,
    data: currency,
  }
}

export const mapCurrencyUserItem = (currency: ICurrency) => {
  const value = currency?.symbol ?? currency?.code ?? buildCurrencyValue(currency)
  return {
    value,
    key: value,
    label: <Typography.Text>{value}</Typography.Text>,
    data: currency,
  }
}

export const mapCustomerItem = (customer: ICustomer, index: number) => ({
  value: buildCustomerValue(customer),
  // Stable unique key — index caused stale dropdown labels when search results changed (React reused rows 0,1).
  key: customer.id !== undefined && customer.id !== null ? String(customer.id) : `customer-${index}-${customer.fullName}`,
  label: <Typography.Text>{buildCustomerValue(customer)}</Typography.Text>,
  data: customer,
})

export const mapCompanyItem = (company: ICompany, index: number) => ({
  value: company.name,
  key: index,
  label: <Typography.Text>{company.name}</Typography.Text>,
  data: company,
})

export const mapCompanyUserItem = (company: ICompany, index: number) => {
  const label = ((company?.fullName ?? company?.refUser?.fullName) || '') + " - " + company?.name + " - " + company?.country + " - " + company?.id;

  return {
    value: String(label),
    key: index,
    label: <Typography.Text>{label}</Typography.Text>,
    data: company,
  };
}

export const mapRemitterItem = (remitter: IRemitter, index: number) => ({
  value: remitter.name,
  key: index,
  label: <Typography.Text>{remitter.name}</Typography.Text>,
  data: remitter,
})

export const mapBankItem = (bank: IBank, index: number) => ({
  value: bank.name,
  key: index,
  label: <Typography.Text>{bank.name}</Typography.Text>,
  data: bank,
})

export const mapBankAccountItem = (account: IBankAccount, index: number) => {
  const value = buildBankAccountValue(account)
  return {
    value,
    key: index,
    label: <Typography.Text>{value}</Typography.Text>,
    data: account,
  }
}

export const buildWalletValue = ({
  address,
  name,
}: IWallet) => {
  return address + " - " + name;
}

export const mapWalletItem = (wallet: IWallet, index: number) => {
  const value = buildWalletValue(wallet)
  return {
    value,
    key: index,
    label: <Typography.Text>{value}</Typography.Text>,
    data: wallet,
  }
}

export const mapCurrencyToken = (currency: ICurrencyToken, index: number) => ({
  value: currency.symbol,
  key: index,
  label: <Typography.Text>{currency.symbol}</Typography.Text>,
  data: currency,
})

export const mapCountry = (
  country: { value: string; label: string },
  index: number
) => ({
  value: country.label,
  key: index,
  label: <Typography.Text>{country.label}</Typography.Text>,
  data: country,
})

export const mapNetwork = (network: INetwork, index: number) => ({
  value: network.name,
  key: index,
  label: <Typography.Text>{network.name}</Typography.Text>,
  data: network,
})

export const editCurrencyRules = (status: PaymentStatus): FormRule[] => [
  () => ({
    validator(_: any, { data }: FieldValue<ICurrency>) {
      if (data || status === PaymentStatus.DRAFT) {
        return Promise.resolve()
      }
      return Promise.reject(new Error('Please select currency'))
    },
  }),
]

export const editCustomerRules: FormRule[] = [
  () => ({
    validator(_: any, { data }: FieldValue<ICustomer>) {
      if (data) {
        return Promise.resolve()
      }
      return Promise.reject(new Error(`Please select customer`))
    },
  }),
]

export const editInvoicePayableServiceProviderRules = (
  status?: PaymentStatus,
  nextStatus?: PaymentStatus
): FormRule[] => [
    () => ({
      validator(_: any, { data }: FieldValue<ICompany>) {
        if (
          data?.deleted &&
          (status === PaymentStatus.DRAFT || status === PaymentStatus.PENDING)
        ) {
          return Promise.reject(
            new Error(
              `This data is not valid or does not exist anymore. Please enter or select a new Service provider`
            )
          )
        }
        if (
          data ||
          nextStatus === PaymentStatus.PENDING ||
          nextStatus === PaymentStatus.REJECTED
        ) {
          return Promise.resolve()
        }
        return Promise.reject(new Error(`Choose service provider`))
      },
    }),
  ]

export const editCompanyRules = (
  status?: PaymentStatus,
  nextStatus?: PaymentStatus,
  entityName = 'company'
): FormRule[] => [
    () => ({
      validator(_: any, { data }: FieldValue<ICompany>) {
        if (
          data?.deleted &&
          (status === PaymentStatus.DRAFT || status === PaymentStatus.PENDING)
        ) {
          const capitalizedFirstLetterEntityName = `${entityName[0].toUpperCase()}${entityName.substring(
            1,
            entityName.length
          )}`
          return Promise.reject(
            new Error(
              `This data is not valid or does not exist anymore. Please enter or select a new ${capitalizedFirstLetterEntityName}`
            )
          )
        }
        if (data || nextStatus === PaymentStatus.DRAFT) {
          return Promise.resolve()
        }
        return Promise.reject(new Error(`Please select ${entityName}`))
      },
    }),
  ]

export const editBankRules = (
  status?: PaymentStatus,
  nextStatus?: PaymentStatus
): FormRule[] => [
    () => ({
      validator(_: any, { data }: FieldValue<IBank>) {
        if (
          data?.deleted &&
          (status === PaymentStatus.DRAFT || status === PaymentStatus.PENDING)
        ) {
          return Promise.reject(
            new Error(
              `This data is not valid or does not exist anymore. Please enter or select a new Bank`
            )
          )
        }
        if (data || nextStatus === PaymentStatus.DRAFT) {
          return Promise.resolve()
        }
        return Promise.reject(new Error('Please select bank'))
      },
    }),
  ]

export const editBankAccountRules = (
  status?: PaymentStatus,
  nextStatus?: PaymentStatus
): FormRule[] => [
    () => ({
      validator(_: any, { data }: FieldValue<IBankAccount>) {
        if (
          data?.deleted &&
          (status === PaymentStatus.DRAFT || status === PaymentStatus.PENDING)
        ) {
          return Promise.reject(
            new Error(
              `This data is not valid or does not exist anymore. Please enter or select a new Bank account`
            )
          )
        }
        if (data || nextStatus === PaymentStatus.DRAFT) {
          return Promise.resolve()
        }
        return Promise.reject(new Error('Please select bank account'))
      },
    }),
  ]

export const serviceProviderDeletedHelpMessage = `This data is not valid or does not exist anymore. Please select a new Service provider or save to empty the field`
export const getWarningHelp = (message: string) => {
  return <span style={{ color: '#ffa940' }}>{message}</span>
}

export const paymentRequestEditQueryOptions: UseQueryOptions<
  GetOneResponse<PaymentRequestsEditForm>,
  HttpError
> = {
  select: (
    data: GetOneResponse<any>
  ): GetOneResponse<PaymentRequestsEditForm> => {
    const { data: loadData } = data || {}
    // const status: PaymentStatus = loadData.status;
    // const editable =
    //   status === PaymentStatus.DRAFT || status === PaymentStatus.PENDING;

    window.dispatchEvent(new CustomEvent('updateUnreadCounts'))
    return {
      data: loadData,
    }
  },
}

export const paymentOrderEditQueryOptions: UseQueryOptions<
  GetOneResponse<PaymentOrderEditForm>,
  HttpError
> = {
  select: (data: GetOneResponse<any>) => {
    const { data: loadData } = data || {}
    // const status: PaymentStatus = loadData.status;
    // const editable =
    //   status === PaymentStatus.DRAFT || status === PaymentStatus.PENDING;

    window.dispatchEvent(new CustomEvent('updateUnreadCounts'))
    return {
      data: loadData,
    }
  },
}
